import type { RegionRequest, StopPreview, Tour, TourStop } from '../../shared/types.js';
import { bboxAreaKm2 } from '../../shared/projection.js';
import { clear, el, icon } from './dom.js';
import { compassPoint } from './regionPicker.js';
import type { FixtureInfo } from './lobbyScreen.js';
import type { RaceWizard } from './raceWizard.js';
import { firstBlocker, type Check } from './raceChecks.js';
import { nudge, paintBanner, paintChecklist, paintStepper } from './steps.js';
import { previewSvg } from './tourPreview.js';
import { plural, serverText, t } from '../i18n/i18n.js';

/**
 * Building a cup: a name, a list of places to race in order, and saving it.
 *
 * Three steps rather than one form. The old builder put the name, the race list, the
 * built-in regions, a search box, a map, the route controls and Save on one scrolling
 * modal, which was everything at once and an explanation of nothing. Now each step asks one
 * thing, and the map — the part that actually takes work — opens full-screen in the race
 * wizard over the top of this, once per race. See DECISIONS.md §63.
 *
 * It is a dialog over the lobby rather than a screen of its own, because making a tour is
 * something you do *while* a lobby full of people waits — and coming back to find the room
 * gone would be its own small disaster.
 *
 * Saving needs an account, and a tour can only be edited by whoever made it, so the author
 * is not a field on this form — it is whoever is signed in. See DECISIONS.md §29.
 */

export interface TourBuilderOptions {
  wizard: RaceWizard;
  /** Saved successfully — the caller refreshes its list and usually selects the result. */
  onSaved(tour: Tour): void;
  onDeleted(id: string): void;
  onSignIn(): void;
}

interface DraftStop {
  title: string;
  region: RegionRequest;
  /** Set for a built-in region, so the row can say so rather than printing coordinates. */
  fixtureTitle?: string;
  /** The card sketch, if the dry run got far enough to produce one. */
  preview?: StopPreview;
}

type StepId = 'name' | 'races' | 'save';
const STEPS: { id: StepId; label: string }[] = [
  { id: 'name', label: t('Name') },
  { id: 'races', label: t('Races') },
  { id: 'save', label: t('Save') },
];

export class TourBuilder {
  readonly root: HTMLElement;

  private readonly heading = el('h2', { class: 'wizard-title' }, t('New tour'));
  private readonly stepper = el('ol', { class: 'stepper', id: 'tour-stepper' });
  private readonly body = el('div', { class: 'builder-step' });
  private readonly checklist = el('ul', { class: 'checklist', id: 'tour-checklist' });
  private readonly banner = el('div', { class: 'banner-alert is-hidden', id: 'tour-error', role: 'alert' });
  private readonly backButton: HTMLButtonElement;
  private readonly nextButton: HTMLButtonElement;
  private readonly stepCount = el('span', { class: 'wizard-step-count' });

  private readonly nameInput: HTMLInputElement;
  private readonly stopList = el('ol', { class: 'race-list', id: 'tour-stops' });
  private readonly addButton: HTMLButtonElement;
  private readonly addNote = el('p', { class: 'field-support', id: 'tour-add-note', role: 'status' });
  private readonly deleteButton: HTMLButtonElement;

  private fixtures: FixtureInfo[] = [];
  private stops: DraftStop[] = [];
  private editingId: string | null = null;
  private maxStops = 8;
  private accountName: string | null = null;
  private step: StepId = 'name';
  private attempted = false;
  /** A save that failed on the server, said in the banner until something changes. */
  private serverError = '';
  private saving = false;

  constructor(private readonly options: TourBuilderOptions) {
    this.nameInput = el('input', {
      class: 'field',
      type: 'text',
      maxLength: 48,
      id: 'tour-name',
      placeholder: t('Kraków Cup'),
      autocomplete: 'off',
    });
    this.nameInput.addEventListener('input', () => this.paint());
    this.nameInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') void this.next();
    });

    this.addButton = el(
      'button',
      { class: 'button button-tonal', type: 'button', id: 'tour-add-stop' },
      icon('add'),
      t('Add a race'),
    );
    this.addButton.addEventListener('click', () => void this.addStop());

    this.deleteButton = el(
      'button',
      { class: 'button button-ghost button-danger', type: 'button', id: 'tour-delete' },
      icon('delete'),
      t('Delete this tour'),
    );
    this.deleteButton.addEventListener('click', () => void this.remove());

    const closeButton = el('button', { class: 'icon-button', type: 'button', id: 'tour-cancel', title: t('Close') }, icon('close'));
    closeButton.setAttribute('aria-label', t('Close'));
    closeButton.addEventListener('click', () => this.close());

    this.backButton = el('button', { class: 'button button-ghost', type: 'button', id: 'tour-back' }, icon('arrow_back'), t('Back'));
    this.backButton.addEventListener('click', () => this.back());
    this.nextButton = el('button', { class: 'button button-primary', type: 'button', id: 'builder-next' }, t('Next'));
    this.nextButton.addEventListener('click', () => void this.next());

    this.root = el(
      'div',
      { class: 'fullscreen-dialog builder is-hidden', id: 'tour-builder', role: 'dialog' },
      el('header', { class: 'top-app-bar' }, closeButton, this.heading, this.stepper),
      el(
        'div',
        { class: 'builder-body' },
        el(
          'div',
          { class: 'builder-column' },
          this.body,
          el(
            'div',
            { class: 'wizard-requirements' },
            el('h3', { class: 'wizard-requirements-title' }, t('To continue')),
            this.checklist,
          ),
          this.banner,
          el('div', { class: 'wizard-actions' }, this.backButton, this.stepCount, this.nextButton),
        ),
      ),
    );
  }

  /**
   * Who is signed in, or null.
   *
   * The server is what actually refuses an unauthenticated save; this only makes the refusal
   * visible before somebody has spent ten minutes drawing four races.
   */
  setAccount(name: string | null): void {
    this.accountName = name;
    if (this.isOpen) {
      this.renderStep();
      this.paint();
    }
  }

  setFixtures(fixtures: FixtureInfo[], maxStops: number): void {
    this.fixtures = fixtures;
    this.maxStops = maxStops;
  }

  open(tour: Tour | null): void {
    this.editingId = tour?.id ?? null;
    this.heading.textContent = tour ? t('Edit “{name}”', { name: tour.name }) : t('New tour');
    this.nameInput.value = tour?.name ?? '';
    this.stops = (tour?.stops ?? []).map((stop, index) => ({
      title: stop.title,
      region: stop.region,
      fixtureTitle: stop.region.fixture
        ? this.fixtures.find((f) => f.id === stop.region.fixture)?.title
        : undefined,
      // Carried through an edit rather than rebuilt: reopening a cup should not spend a
      // minute regenerating four cities to redraw pictures it already has.
      ...(tour?.preview?.[index] ? { preview: tour.preview[index] } : {}),
    }));
    this.serverError = '';
    this.root.classList.remove('is-hidden');
    // An existing tour opens on its races, because that is what anybody editing one is after.
    this.goTo(tour ? 'races' : 'name');
  }

  close(): void {
    this.root.classList.add('is-hidden');
  }

  get isOpen(): boolean {
    return !this.root.classList.contains('is-hidden');
  }

  // -------------------------------------------------------------------------
  // Steps
  // -------------------------------------------------------------------------

  private goTo(step: StepId): void {
    this.step = step;
    this.attempted = false;
    this.renderStep();
    this.paint();
    if (step === 'name') this.nameInput.focus();
  }

  private back(): void {
    const index = STEPS.findIndex((s) => s.id === this.step);
    if (index === 0) this.close();
    else this.goTo(STEPS[index - 1].id);
  }

  private async next(): Promise<void> {
    // Pressing Save again is the retry, so the last attempt's refusal must not block it.
    this.serverError = '';
    const checks = this.checksFor(this.step);
    const blocker = firstBlocker(checks);
    if (blocker) {
      this.refuse(blocker);
      return;
    }
    const index = STEPS.findIndex((s) => s.id === this.step);
    if (index < STEPS.length - 1) this.goTo(STEPS[index + 1].id);
    else await this.save();
  }

  private refuse(blocker: Check): void {
    this.attempted = true;
    this.paint();
    if (blocker.target === 'name' && this.step === 'name') this.nameInput.focus();
    if (blocker.target === 'list') this.addButton.focus();
    nudge(this.banner);
  }

  private jump(target: StepId): void {
    const to = STEPS.findIndex((s) => s.id === target);
    const from = STEPS.findIndex((s) => s.id === this.step);
    if (to <= from) {
      this.goTo(target);
      return;
    }
    for (let i = from; i < to; i++) {
      const blocker = firstBlocker(this.checksFor(STEPS[i].id));
      if (blocker) {
        if (STEPS[i].id !== this.step) this.goTo(STEPS[i].id);
        this.refuse(blocker);
        return;
      }
    }
    this.goTo(target);
  }

  private checksFor(step: StepId): Check[] {
    const named: Check = {
      id: 'name',
      label: t('Give the tour a name'),
      ok: this.nameInput.value.trim().length > 0,
      level: 'block',
      problem:
        step === 'name'
          ? t('The tour needs a name — it is what everybody sees on the shelf. Type one above.')
          : t('The tour has no name yet. Go back to the first step and give it one.'),
      target: 'name',
    };
    const raced: Check = {
      id: 'races',
      label: t('Add at least one race'),
      ok: this.stops.length > 0,
      level: 'block',
      problem:
        step === 'races'
          ? t('A tour with no races has nothing to drive. Press “Add a race” to pick the first one.')
          : t('There are no races in this tour yet. Go back a step and add one.'),
      target: 'list',
    };
    if (step === 'name') return [named];
    if (step === 'races') return [raced];
    return [
      named,
      raced,
      {
        id: 'account',
        label: t('Be signed in'),
        ok: this.accountName !== null,
        level: 'block',
        problem: t(
          'Saving a tour needs an account, so that it has somebody to belong to and nobody else can change it. Press “Sign in” above — your races are kept while you do.',
        ),
      },
      ...(this.serverError
        ? [
            {
              id: 'server',
              label: t('Saved on the server'),
              ok: false,
              level: 'block' as const,
              problem: this.serverError,
            },
          ]
        : []),
    ];
  }

  private paint(): void {
    const index = STEPS.findIndex((s) => s.id === this.step);
    paintStepper(this.stepper, STEPS, index, (id) => this.jump(id as StepId));
    const checks = this.checksFor(this.step);
    // A server refusal is news whether or not Save was pressed twice.
    paintChecklist(this.checklist, checks, this.attempted || Boolean(this.serverError));
    paintBanner(this.banner, checks, this.attempted || Boolean(this.serverError));
    this.stepCount.textContent = t('Step {n} of {total}', { n: index + 1, total: STEPS.length });
    this.backButton.lastChild!.textContent = index === 0 ? t('Cancel') : t('Back');
    clear(this.nextButton);
    if (this.step === 'save') {
      this.nextButton.id = 'tour-save';
      this.nextButton.append(icon('check'), this.saving ? t('Saving…') : this.editingId ? t('Save changes') : t('Save tour'));
    } else {
      // Not `tour-next`: that is the results screen's "Next race", and both are in the page.
      this.nextButton.id = 'builder-next';
      this.nextButton.append(t('Next'), icon('arrow_forward'));
    }
    this.nextButton.classList.toggle('is-pending', firstBlocker(checks) !== null);
  }

  private renderStep(): void {
    clear(this.body);
    const head = (title: string, intro: string) =>
      this.body.append(
        el('p', { class: 'wizard-overline' }, STEPS.find((s) => s.id === this.step)!.label),
        el('h3', { class: 'wizard-step-title' }, title),
        el('p', { class: 'wizard-intro' }, intro),
      );

    if (this.step === 'name') {
      head(
        this.editingId ? t('The tour’s name') : t('Name your tour'),
        t('A tour is a playlist of races run back to back for points, Mario Kart style. Everybody on this server can race it; only you can change it.'),
      );
      this.body.append(
        el('label', { class: 'field-label', htmlFor: 'tour-name' }, t('Tour name'), this.nameInput),
        el('p', { class: 'field-support' }, t('Up to 48 characters. It is what everybody sees on the shelf.')),
        this.accountCard(),
      );
    } else if (this.step === 'races') {
      head(
        t('Add the races'),
        t('Up to {max}, raced in this order. Each one is a built-in region or a box you draw on the map, and you can shape its route as you add it.', { max: this.maxStops }),
      );
      this.renderStops();
      this.body.append(this.stopList, el('div', { class: 'race-list-foot' }, this.addButton, this.addNote));
      this.paintAddButton();
    } else {
      head(
        t('Save the tour'),
        t('Check it over. Saving puts it on the shelf for everybody on this server, and selects it in your lobby.'),
      );
      this.body.append(this.summary(), this.accountCard());
      if (this.editingId) this.body.append(el('div', { class: 'danger-zone' }, this.deleteButton));
    }
  }

  private accountCard(): HTMLElement {
    if (this.accountName) {
      return el(
        'div',
        { class: 'info-card', id: 'tour-author' },
        icon('account_circle', 'info-card-icon'),
        el('p', {}, t('Saved as '), el('strong', {}, this.accountName)),
      );
    }
    const signIn = el('button', { class: 'button button-tonal', type: 'button' }, icon('passkey'), t('Sign in'));
    signIn.addEventListener('click', () => this.options.onSignIn());
    return el(
      'div',
      { class: 'info-card is-warning', id: 'tour-author' },
      icon('lock', 'info-card-icon'),
      el(
        'div',
        { class: 'info-card-text' },
        el('p', {}, t('Saving a tour needs an account. You can build it now and sign in before you save.')),
        signIn,
      ),
    );
  }

  private summary(): HTMLElement {
    const name = this.nameInput.value.trim();
    return el(
      'div',
      { class: 'summary-card' },
      el('span', { class: 'summary-art' }, previewSvg(this.stops[0]?.preview)),
      el(
        'div',
        { class: 'summary-text' },
        el('h4', { class: 'summary-title' }, name || t('Untitled tour')),
        el('p', { class: 'summary-meta' }, plural(this.stops.length, '{n} race', '{n} races')),
        el(
          'ol',
          { class: 'summary-stops' },
          ...this.stops.map((stop) => el('li', {}, el('strong', {}, stop.title), ' · ', describeRegion(stop))),
        ),
      ),
    );
  }

  // -------------------------------------------------------------------------
  // Races
  // -------------------------------------------------------------------------

  private paintAddButton(): void {
    const full = this.stops.length >= this.maxStops;
    this.addButton.classList.toggle('is-pending', full);
    this.addNote.textContent = t('{n} of {max} races', { n: this.stops.length, max: this.maxStops });
  }

  /**
   * Opens the race wizard for a new stop.
   *
   * Full is explained, not disabled: the button still answers, with the reason and what to do.
   */
  private async addStop(): Promise<void> {
    if (this.stops.length >= this.maxStops) {
      this.addNote.textContent = plural(
        this.maxStops,
        'A tour holds at most {n} races. Remove one to make room for another.',
        'A tour holds at most {n} races. Remove one to make room for another.',
      );
      this.addNote.classList.add('is-error');
      nudge(this.addNote);
      return;
    }
    this.addNote.classList.remove('is-error');
    const n = this.stops.length + 1;
    const name = this.nameInput.value.trim();
    const result = await this.options.wizard.open({
      mode: 'stop',
      title: name ? t('Add race {n} to “{name}”', { n, name }) : t('Add race {n}', { n }),
      confirmLabel: t('Add to tour'),
      defaultTitle: t('Race {n}', { n }),
    });
    if (!result) return;
    this.stops.push(result);
    this.serverError = '';
    this.goTo('races');
  }

  private async editStop(index: number): Promise<void> {
    const stop = this.stops[index];
    const result = await this.options.wizard.open({
      mode: 'stop',
      title: t('Edit “{name}”', { name: stop.title }),
      confirmLabel: t('Save race'),
      initial: { region: stop.region, title: stop.title },
      startAt: 'pins',
    });
    if (!result) return;
    // A race that was not rebuilt keeps the sketch it had rather than losing it.
    this.stops[index] = { ...result, ...(result.preview ? {} : stop.preview ? { preview: stop.preview } : {}) };
    this.goTo('races');
  }

  private renderStops(): void {
    clear(this.stopList);
    if (!this.stops.length) {
      this.stopList.append(
        el(
          'li',
          { class: 'race-item is-empty' },
          icon('route', 'race-empty-icon'),
          t('No races yet. Add the first one below.'),
        ),
      );
      return;
    }
    for (const [index, stop] of this.stops.entries()) {
      const button = (glyph: Parameters<typeof icon>[0], title: string, onClick: () => void, disabled = false) => {
        const b = el('button', { class: 'icon-button', type: 'button', title, disabled }, icon(glyph));
        b.setAttribute('aria-label', title);
        b.addEventListener('click', onClick);
        return b;
      };
      this.stopList.append(
        el(
          'li',
          { class: 'race-item' },
          el('span', { class: 'race-index' }, String(index + 1)),
          el('span', { class: 'race-art' }, previewSvg(stop.preview)),
          el(
            'span',
            { class: 'race-text' },
            el('span', { class: 'race-title' }, stop.title),
            el('span', { class: 'race-where' }, describeRegion(stop)),
          ),
          el(
            'span',
            { class: 'race-actions' },
            button('edit', t('Edit'), () => void this.editStop(index)),
            button('arrow_upward', t('Move up'), () => this.move(index, -1), index === 0),
            button('arrow_downward', t('Move down'), () => this.move(index, 1), index === this.stops.length - 1),
            button('delete', t('Remove'), () => {
              this.stops.splice(index, 1);
              this.goTo('races');
            }),
          ),
        ),
      );
    }
  }

  private move(index: number, by: number): void {
    const to = index + by;
    if (to < 0 || to >= this.stops.length) return;
    const [row] = this.stops.splice(index, 1);
    this.stops.splice(to, 0, row);
    this.renderStops();
  }

  // -------------------------------------------------------------------------
  // Saving
  // -------------------------------------------------------------------------

  private async save(): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    this.serverError = '';
    this.paint();
    try {
      const payload = {
        ...(this.editingId ? { id: this.editingId } : {}),
        name: this.nameInput.value.trim(),
        stops: this.stops.map(
          (stop, index): Omit<TourStop, 'id'> => ({ title: stop.title || t('Race {n}', { n: index + 1 }), region: stop.region }),
        ),
        // All or nothing: the server wants one sketch per stop, and a cup where three cards
        // have a map and the fourth has a hole would look broken rather than incomplete.
        ...(this.stops.every((stop) => stop.preview)
          ? { preview: this.stops.map((stop) => stop.preview!) }
          : {}),
      };
      const res = await fetch('/api/tours', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = (await res.json()) as { ok: boolean; error?: string; tour?: Tour };
      if (!body.ok || !body.tour) {
        this.serverError = t('The server would not save it: {reason}', {
          reason: body.error ? serverText(body.error) : `HTTP ${res.status}`,
        });
        return;
      }
      this.options.onSaved(body.tour);
      this.close();
    } catch (err) {
      this.serverError = t('Could not reach the server to save ({error}). Your races are still here — try again.', {
        error: (err as Error).message,
      });
    } finally {
      this.saving = false;
      if (this.isOpen) this.paint();
    }
  }

  private async remove(): Promise<void> {
    const id = this.editingId;
    if (!id) return;
    // No undo, and no second copy anywhere, so this is one of the few places in the game
    // worth stopping someone with a dialog.
    if (!window.confirm(t('Delete “{name}”? This cannot be undone.', { name: this.nameInput.value.trim() }))) return;
    try {
      await fetch(`/api/tours/${encodeURIComponent(id)}`, { method: 'DELETE' });
      this.options.onDeleted(id);
      this.close();
    } catch (err) {
      this.serverError = t('Could not delete it: {error}', { error: (err as Error).message });
      this.paint();
    }
  }
}

function describeRegion(stop: DraftStop): string {
  // A race named after its built-in region would otherwise say the name twice in a row.
  const where = stop.fixtureTitle
    ? stop.fixtureTitle === stop.title
      ? t('Built-in region')
      : t('{name} · built in', { name: stop.fixtureTitle })
    : stop.region.fixture
      ? t('{name} · built in', { name: stop.region.fixture })
      : whereBox(stop.region.bbox);
  return [where, ...routeNotes(stop.region)].join(' · ');
}

function whereBox(bbox: DraftStop['region']['bbox']): string {
  const lat = (bbox.south + bbox.north) / 2;
  const lon = (bbox.west + bbox.east) / 2;
  return `${lat.toFixed(4)}, ${lon.toFixed(4)} · ${bboxAreaKm2(bbox).toFixed(2)} km²`;
}

/**
 * The bits of the race that are not the region — said on the row, because two stops in the
 * same city with different directions are otherwise two identical lines.
 */
function routeNotes(region: DraftStop['region']): string[] {
  const out: string[] = [];
  if (region.startBearing !== undefined) out.push(t('starts {dir}', { dir: compassPoint(region.startBearing) }));
  if (region.checkpoints?.length) out.push(plural(region.checkpoints.length, '{n} placed checkpoints', '{n} placed checkpoints'));
  else if (region.checkpointCount !== undefined) out.push(plural(region.checkpointCount, '{n} checkpoints', '{n} checkpoints'));
  return out;
}
